import { isValidElement, type ReactNode } from 'react';

// ── route-aware document title + focus (design-grammar §4.4, §4.8) ─────────

/** The site suffix every title carries. */
export const TITLE_SUFFIX = 'TRF';

/**
 * The depth signal. §4.4: a depth-2 page or hub child qualifies its eyebrow
 * `{canonical parent's label} · {kind}` — "Families · Profile". The presence
 * of that separator is how `PageHead` knows it is not at depth 0.
 */
const DEPTH_SEPARATOR = ' · ';

/** Flatten a ReactNode title to the text a browser tab can carry. */
export function titleText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(titleText).join('');
  if (isValidElement(node)) return titleText((node.props as { children?: ReactNode }).children);
  return '';
}

/**
 * `document.title` from the same strings the page already shows (§4.4).
 *
 *   depth 0 (`eyebrow` = "Finances")          → "Your money · TRF"
 *   depth 2 (`eyebrow` = "Families · Profile") → "Ortega family · Families · TRF"
 *
 * The section comes from the eyebrow's LEFT side, which §4.4 defines as the
 * canonical parent's sidebar label — so forty open family tabs are forty
 * distinct titles that still say which surface they belong to.
 *
 * Known residue: §4.4 also allows `·` inside a depth-0 nav label when the item
 * is a sub-surface of a sibling ("Threads · Live"). Such a page reads
 * "{H1} · Threads · TRF" here, which is true rather than wrong — and
 * `documentTitle` is the escape hatch either way.
 */
export function buildDocumentTitle(head: string, eyebrow: string): string {
  const name = head.replace(/\s+/g, ' ').trim();
  const eyebrowText = eyebrow.replace(/\s+/g, ' ').trim();
  const section = eyebrowText.includes(DEPTH_SEPARATOR)
    ? eyebrowText.split(DEPTH_SEPARATOR)[0].trim()
    : '';
  return [name, section === name ? '' : section, TITLE_SUFFIX]
    .filter(Boolean)
    .join(DEPTH_SEPARATOR);
}

/**
 * The last pathname any `PageHead` observed, for the whole app session.
 *
 * It has to live at MODULE scope, not in the component: React Router mounts a
 * FRESH element for a new route, so a per-instance ref initialises to the new
 * pathname and can never see a change — focus would move on a `:id` swap and
 * nowhere else. This records "where the app was", which is app-level state.
 *
 * It is the PREVIOUS-VALUE guard from the #1321 `EligibilityCheck` fix, not a
 * "have I painted before" boolean: `<StrictMode>` double-invokes a mount
 * effect with no cleanup in between, and a boolean flips on the first
 * invocation so the second reads "already painted" and steals focus on
 * arrival. Comparing pathnames is idempotent under the double-invoke — both
 * invocations see the same value — while a real navigation is still caught.
 */
export const pageHeadNavigationState: { lastPathname: string | null } = { lastPathname: null };

/** Test-only: clear the session-scoped pathname record between cases. */
export function __resetPageHeadNavigationState() {
  pageHeadNavigationState.lastPathname = null;
}
